/**
 * WhyChooseUsSection Component
 * Displays key features and membership CTA
 */

import React from 'react';

export const WhyChooseUsSection: React.FC = () => {
  const features = [
    {
      id: 'certified',
      icon: (
        <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
        </svg>
      ),
      title: 'Certified Jewelry',
      description: 'Every stone and initial comes with internationally recognized certification.',
    },
    {
      id: 'hallmarked',
      icon: (
        <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ),
      title: 'Hallmarked Gold',
      description: 'BIS Hallmarked gold ensuring the highest standards of purity and value.',
    },
    {
      id: 'custom',
      icon: (
        <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
          <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
        </svg>
      ),
      title: 'Custom Design',
      description: 'Bring your vision to life with our expert bespoke design services.',
    },
    {
      id: 'legacy',
      icon: (
        <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
        </svg>
      ),
      title: 'Trusted Legacy',
      description: 'A reputation built over 50 years of honesty and customer satisfaction.',
    },
  ];

  const handleJoinCircle = () => {
    // Scroll to contact section or open membership form
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="why-us" className="py-20 px-6 bg-black">
      <div className="max-w-7xl mx-auto">
        {/* Feature Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {features.map((feature) => (
            <div key={feature.id} className="text-center">
              {/* Icon */}
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-yellow-500 rounded-full flex items-center justify-center text-black">
                  {feature.icon}
                </div>
              </div>

              {/* Title */}
              <h3 className="text-xl font-semibold text-white mb-3">
                {feature.title}
              </h3>

              {/* Description */}
              <p className="text-gray-400 text-sm leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>

        {/* Membership CTA Banner */}
        <div className="bg-yellow-500 rounded-2xl p-8 md:p-12 flex flex-col md:flex-row items-center justify-between">
          {/* Text Content */}
          <div className="mb-6 md:mb-0 md:mr-8">
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-4 uppercase">
              Siaram Golden Membership
            </h2>
            <p className="text-black text-lg max-w-2xl">
              Join our exclusive circle for priority access to new collections, 
              special pricing, and complimentary jewellery maintenance.
            </p>
          </div>

          {/* CTA Button */}
          <button
            onClick={handleJoinCircle}
            className="bg-black text-white px-8 py-4 rounded-lg font-bold uppercase text-sm tracking-wider hover:bg-gray-900 transition-all duration-300 whitespace-nowrap shadow-lg"
            aria-label="Join the circle"
          >
            Join The Circle
          </button>
        </div>
      </div>
    </section>
  );
};
